import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CircleNotch } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { apiSend } from "@/lib/api-error";
import { formatCurrency, formatDateTimeBs } from "@/lib/format";
import { toast } from "@/lib/toast";
import { ApproveDialog, RejectDialog } from "./ResolveDialogs";

interface CancellationRequest {
  id: string;
  sessionId: string;
  reason: string;
  requestedAt: string;
  resolvedAt: string | null;
  resolution: "approved" | "rejected" | "withdrawn" | null;
  adminNote: string | null;
  feeApplied: number | null;
  scheduledAt: string;
  sessionStatus: string;
  paymentMethod: string | null;
  priceCharged: number | null;
  therapistName: string;
  clientName: string;
}

const RESOLUTION_LABEL: Record<string, string> = {
  approved: "Odobreno",
  rejected: "Odbijeno",
  withdrawn: "Povučeno",
};

export function CancellationRequestsPage() {
  const queryClient = useQueryClient();
  const [tab, setTab] = useState<"pending" | "resolved">("pending");
  const [approving, setApproving] = useState<CancellationRequest | null>(null);
  const [rejecting, setRejecting] = useState<CancellationRequest | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ["admin", "cancellation-requests", tab],
    queryFn: () =>
      fetch(`/api/admin/cancellation-requests?state=${tab}`, { credentials: "include" })
        .then((r) => (r.ok ? r.json() : Promise.reject(new Error(String(r.status)))))
        .then((p) => p.items as CancellationRequest[]),
  });

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ["admin", "cancellation-requests"] });
    queryClient.invalidateQueries({ queryKey: ["admin-stats"] });
  };

  const approveMutation = useMutation({
    mutationFn: ({ id, applyFee }: { id: string; applyFee: boolean }) =>
      apiSend(`/api/admin/cancellation-requests/${id}/approve`, "POST", { applyFee }, "Greška pri odobravanju"),
    onSuccess: () => {
      invalidate();
      setApproving(null);
      toast.success("Otkazivanje je odobreno");
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const rejectMutation = useMutation({
    mutationFn: ({ id, note }: { id: string; note: string }) =>
      apiSend(`/api/admin/cancellation-requests/${id}/reject`, "POST", { note }, "Greška pri odbijanju"),
    onSuccess: () => {
      invalidate();
      setRejecting(null);
      toast.success("Zahtjev je odbijen");
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const items = data ?? [];
  const now = new Date().toISOString();

  return (
    <div className="mx-auto max-w-6xl space-y-5 pb-14">
      <header className="space-y-2">
        <h1 className="text-3xl font-semibold tracking-tight text-foreground" style={{ fontFamily: "var(--font-heading)" }}>
          Zahtjevi za otkazivanje
        </h1>
        <p className="max-w-2xl text-sm text-muted-foreground">
          Terapeut je zatražio otkazivanje plaćenog termina. Odobrenje otkazuje termin i pokreće povrat novca klijentu.
        </p>
      </header>

      <div className="flex gap-2">
        {(["pending", "resolved"] as const).map((s) => (
          <Button key={s} size="sm" variant={tab === s ? "default" : "outline"} onClick={() => setTab(s)}>
            {s === "pending" ? "Na čekanju" : "Obrađeni"}
          </Button>
        ))}
        {isLoading && <CircleNotch className="size-4 animate-spin text-muted-foreground" />}
      </div>

      {items.length === 0 && !isLoading ? (
        <div className="rounded-2xl border border-dashed border-border/60 bg-card py-16 text-center text-sm text-muted-foreground">
          Nema zahtjeva.
        </div>
      ) : (
        <div className="overflow-x-auto rounded-2xl border border-border/60 bg-card">
          <table className="w-full min-w-[900px] text-sm">
            <thead className="text-left text-xs uppercase tracking-wider text-muted-foreground">
              <tr>
                <th className="px-4 py-3">Terapeut</th>
                <th className="px-4 py-3">Klijent</th>
                <th className="px-4 py-3">Termin</th>
                <th className="px-4 py-3">Iznos</th>
                <th className="px-4 py-3">Razlog</th>
                <th className="px-4 py-3">{tab === "pending" ? "Zatraženo" : "Ishod"}</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody>
              {items.map((r) => (
                <tr key={r.id} className="border-t border-border/50">
                  <td className="px-4 py-3 font-medium text-foreground">{r.therapistName}</td>
                  <td className="px-4 py-3">{r.clientName}</td>
                  <td className="px-4 py-3">
                    {formatDateTimeBs(r.scheduledAt)}
                    {tab === "pending" && r.scheduledAt < now && (
                      <span className="ml-2 rounded-full bg-rose-50 px-2 py-0.5 text-[11px] text-rose-700">termin prošao</span>
                    )}
                  </td>
                  <td className="px-4 py-3">{formatCurrency(r.priceCharged ?? 0)}</td>
                  <td className="max-w-[220px] truncate px-4 py-3 text-muted-foreground" title={r.reason}>
                    {r.reason}
                  </td>
                  <td className="px-4 py-3 text-muted-foreground">
                    {tab === "pending"
                      ? formatDateTimeBs(r.requestedAt)
                      : `${RESOLUTION_LABEL[r.resolution ?? ""] ?? ""}${r.adminNote ? `: ${r.adminNote}` : ""}`}
                  </td>
                  <td className="px-4 py-3">
                    {tab === "pending" && (
                      <div className="flex justify-end gap-2">
                        <Button size="sm" variant="outline" onClick={() => setRejecting(r)}>Odbij</Button>
                        <Button size="sm" onClick={() => setApproving(r)}>Odobri</Button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {approving && (
        <ApproveDialog
          clientName={approving.clientName}
          priceCharged={approving.priceCharged ?? 0}
          onClose={() => setApproving(null)}
          onConfirm={(applyFee) => approveMutation.mutate({ id: approving.id, applyFee })}
          isPending={approveMutation.isPending}
        />
      )}
      {rejecting && (
        <RejectDialog
          onClose={() => setRejecting(null)}
          onConfirm={(note) => rejectMutation.mutate({ id: rejecting.id, note })}
          isPending={rejectMutation.isPending}
        />
      )}
    </div>
  );
}
